import { useBreakpoints } from '@vueuse/core'
import { computed } from 'vue'

/**
 * Tailwind CSS default breakpoints
 */
const TAILWIND_BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const

/**
 * Reusable breakpoints composable that provides standardized breakpoints
 * matching Tailwind CSS defaults using VueUse's useBreakpoints
 *
 * @returns Object containing reactive breakpoint utilities and individual breakpoint refs
 */
export const useTailwindBreakpoints = () => {
  // Get VueUse breakpoints with Tailwind defaults
  const breakpoints = useBreakpoints(TAILWIND_BREAKPOINTS)

  /**
   * Check if screen is mobile size (< md: 768px)
   */
  const isMobile = computed(() => breakpoints.smaller('md').value)

  /**
   * Check if screen is tablet size (md to lg: 768px - 1023px)
   */
  const isTablet = computed(() => breakpoints.between('md', 'lg').value)

  /**
   * Check if screen is desktop size (>= lg: 1024px+)
   */
  const isDesktop = computed(() => breakpoints.greaterOrEqual('lg').value)

  /**
   * Check if screen is small mobile (< sm: 640px)
   */
  const isSmallMobile = computed(() => breakpoints.smaller('sm').value)

  /**
   * Check if screen is small desktop (< xl: 1280px)
   */
  const isSmallDesktop = computed(() => breakpoints.smallerOrEqual('xl').value)

  /**
   * Check if screen is large desktop (>= xl: 1280px)
   */
  const isLargeDesktop = computed(() => breakpoints.greaterOrEqual('xl').value)

  /**
   * Check if screen is extra large desktop (>= 2xl: 1536px)
   */
  const isExtraLargeDesktop = computed(() => breakpoints.greaterOrEqual('2xl').value)

  return {
    // Common breakpoint utilities
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isSmallDesktop,
    isLargeDesktop,
    isExtraLargeDesktop,

    // Individual breakpoint refs for granular control
    sm: breakpoints.greaterOrEqual('sm'),
    md: breakpoints.greaterOrEqual('md'),
    lg: breakpoints.greaterOrEqual('lg'),
    xl: breakpoints.greaterOrEqual('xl'),
    '2xl': breakpoints.greaterOrEqual('2xl'),

    // Direct access to VueUse breakpoints for advanced usage
    breakpoints,

    // Breakpoint values for reference
    breakpointValues: TAILWIND_BREAKPOINTS
  }
}
